/**
 * MUI DynamicContent Component
 * 
 * This component is the Material UI equivalent of the original DynamicContent component.
 * It provides dynamic content rendering based on the current state.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import MuiPlainText from '../atoms/PlainText';
import MuiBulletList from '../atoms/BulletList';
import { getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { values } from 'lodash';
import { 
  DynamicContentProps, 
  ErrorContentProps, 
  ValueContentProps 
} from 'types/components/molecules';
import type { RootState } from 'types/redux/state';

/**
 * MUI ErrorContent component
 * 
 * @param {Object} props - Component props
 * @returns {JSX.Element} The MUI ErrorContent component
 */
const MuiErrorContent: React.FC<ErrorContentProps> = (props) => {
  const errors = useSelector((state: RootState) => values(state.ui.error).map(err => err.msg)).flat();
  
  // If there are no errors or only one error, use PlainText
  if (errors.length <= 1) {
    return <MuiPlainText content={errors[0] || ''} />;
  }
  
  // Otherwise use BulletList with the array of errors
  return <MuiBulletList content={errors} />;
};

/**
 * MUI ValueContent component
 * 
 * @param {Object} props - Component props
 * @param {string} props.parameter - The parameter to get the value for
 * @param {Array} props.content - The content to join with the value
 * @returns {JSX.Element} The MUI ValueContent component
 */
const MuiValueContent: React.FC<ValueContentProps> = (props) => {
  const { parameter, content, ...rest } = props;
  
  // Use any type to bypass TS checking for the state parameter
  type AnyState = any;
  
  // Use the selector to get the value from redux state
  const currentValue = useSelector((state: AnyState) => {
    // We need to use any here because we don't have proper type definitions for getCurrentValue2
    return (getCurrentValue2 as any)(state, parameter);
  });
  
  // Safely create the content string
  let contentStr = '';
  if (typeof currentValue === 'string' || typeof currentValue === 'number') {
    // Create parts array by splitting the content on placeholders (if any)
    if (content && content.length > 0) {
      // Just join the array elements with the currentValue as separator
      contentStr = content.join(String(currentValue));
    } else {
      contentStr = String(currentValue);
    }
  }
  
  return <MuiPlainText content={contentStr} {...rest} />;
};

/**
 * MUI DynamicContent component
 * 
 * @param {Object} props - Component props
 * @returns {JSX.Element} The MUI DynamicContent component
 */
const MuiDynamicContent: React.FC<DynamicContentProps> = (props) => {
  return <MuiErrorContent {...props} />;
};

export { MuiErrorContent, MuiValueContent };
export default MuiDynamicContent;
